import { Row, Tabs } from 'antd';
import PropTypes from 'prop-types';
import React, {
  useEffect, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import allActions from '../../../actions';
import * as loadingKeys from '../../../actions/MaterialDetailsDomain/LoadingKeys';
import MaterialDetailsAction from '../../../actions/MaterialDetailsDomain/MaterialDetailsAction';
import hasAuthenticatedRole from '../../../utils/AuthFunctions';
import { getCorrectTokenFromUser } from '../../../utils/token';
import '../MaterialDetails.css';
import styles from '../styles.module.scss';
import FixedBin from './TabComponents/FixedBin';
import LeadTimeTab from './TabComponents/leadTime/LeadTimeTab';
import PlOrdReleaseTab from './TabComponents/plOrdReleaseTab/index';
import TabGraph from './TabComponents/tabGraph/TabGraph';
import { GRAPH_TYPES } from './TabComponents/tabGraph/TabGraphHelpers';
import TableTab from './TabComponents/tableTab/TableTab';
import { getTabOptions } from './TabOptions';
import ConsumptionHistoryContainer from './TabComponents/consumptionTab/ConsumptionHistoryContainer';

function TabElements({ excludePM10 }) {
  const { TabPane } = Tabs;
  const { plant, material } = useParams();
  const tabOptions = getTabOptions(plant, material, excludePM10);
  const [filteredPurchasing, updateFilteredPurchasing] = useState([]);
  const [activeTabKey, setActiveTabKey] = useState('AM');
  const [hasViewerAccess, setHasViewerAccess] = useState(false);
  const [onStartUp, setOnStartUp] = useState(true);
  const defaultTab = useSelector(
    (state) => state.commonState?.userSettings?.TAB_SECTION_DEFAULT_TAB,
  );
  const user = useSelector((state) => state.authState?.user);
  const [clientHeight, setClientHeight] = useState(window.innerHeight - 270);
  const [plOrdRelData, setPlOrdRelData] = useState([]);
  const [openQtyResvalue, setOpenQtyResvalue] = useState([]);

  const dispatch = useDispatch();

  const ref = useRef();

  useEffect(() => {
    if (user?.idTokenClaims?.roles) {
      const userHasAuthenticatedRole = hasAuthenticatedRole(
        user.idTokenClaims.roles,
        process.env.REACT_APP_VIEW_PRICES,
      );
      setHasViewerAccess(userHasAuthenticatedRole);
    }
  }, [user?.idTokenClaims]);

  useEffect(() => {
    if (defaultTab) {
      setActiveTabKey(defaultTab ?? 'AM');
    }
  }, [defaultTab]);

  const changeTab = (key) => {
    setActiveTabKey(key);
  };

  useEffect(() => {
    if (ref && ref.current && onStartUp) {
      if (ref.current.clientHeight > 0) {
        setClientHeight(ref.current.clientHeight - 50);
        setOnStartUp(false);
      }
    }
  }, [ref?.current]);

  useEffect(() => {
    const fetch = async () => {
      const fetchedData = await MaterialDetailsAction.getPlannedOrderChangelogs(
        plant, material, getCorrectTokenFromUser(user), dispatch
      );
      setPlOrdRelData(fetchedData);
    };
    if (plant && material) {
      fetch();
    }
  }, []);

  const materialMovement = useSelector((state) => state.materialDetailsState.materialMovement);
  const consumptionHistory = useSelector((state) => state.materialDetailsState.consumptionHistory);
  const materialChangelogs = useSelector((state) => state.materialDetailsState.materialChangelogs);
  const functionalLocation = useSelector((state) => state.materialDetailsState.functionalLocation);
  const reservations = useSelector((state) => state.materialDetailsState.reservations);
  const purchasing = useSelector((state) => state.materialDetailsState.purchasing);
  const returnDocument = useSelector((state) => state.materialDetailsState.returnDocument);
  const warehouse = useSelector((state) => state.materialDetailsState.warehouse);
  const changelogs = useSelector((state) => state.materialDetailsState.changelogs);
  const xPlant = useSelector((state) => state.materialDetailsState.xPlant);
  const materialLeadTime = useSelector((state) => state.materialDetailsState.materialLeadTime);
  const materialLeadTimeInsight = useSelector((state) => state.materialDetailsState.materialLeadTimeInsight);
  const openDocs = useSelector((state) => state.materialDetailsState.openDocs);

  const fixKeyOnXPlant = xPlant && xPlant.length > 0 ? xPlant.map((x, i) => ({ ...x, key: `${x.key}${i}` })) : [];

  useEffect(() => {
    const qtyRes = reservations?.map((el) => {
      if (el.PLANT_FACILITY_SAP_ID === plant) {
        return el.REQUIRED_QTY;
      }
      return null;
    });
    const ResValues = qtyRes?.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    setOpenQtyResvalue(ResValues);
  }, [reservations]);

  return (
    <Row className="full-tab" style={{ overflowX: 'hidden', height: '100%' }}>
      <Tabs className="matDetsTabs compact" style={{ minWidth: '100%', height: '100%' }} onChange={changeTab} activeKey={activeTabKey}>
        <TabPane forceRender tab={<span className={materialMovement?.length > 0 ? 'hasContent' : 'noContent'}>All movement</span>} key="AM">
          <div id="tabAllMovements" style={{ height: '100%', overflowX: 'hidden', overflowY: 'auto' }} ref={ref}>
            <TableTab
              loadingKey={loadingKeys.materialMovement}
              selector={(state) => state.materialDetailsState.materialMovement}
              action={allActions.MaterialDetailsActions.fetchMaterialMovement}
              title="Material Movement"
              tabOptions={tabOptions.allMovementOptions}
              containerID="tabAllMovements"
              hideSensitiveValues={!hasViewerAccess}
              data={materialMovement}
              clientHeight={clientHeight - 40}
            />
          </div>
        </TabPane>
        <TabPane
          forceRender
          tab={(
            <span className={consumptionHistory?.length > 0 ? 'hasContent' : 'noContent'}>
              Real Consumption
            </span>
          )}
          key="RC"
        >
          <ConsumptionHistoryContainer
            activeTabKey={activeTabKey}
            loadingKey={loadingKeys.consumptionHistory}
            data={consumptionHistory}
            excludePM10={excludePM10}
            clientHeight={(clientHeight * 0.6) - 120}
          />
        </TabPane>
        <TabPane
          forceRender
          tab={(
            <span className={materialChangelogs?.length === 0 && 'noContent'}>
              MRP Changelog
            </span>
          )}
          key="MC"
        >
          <div id="tabContainer3" style={{ height: '100%', overflowX: 'hidden' }}>
            <TableTab
              loadingKey={loadingKeys.materialChangelogs}
              selector={(state) => state.materialDetailsState.materialChangelogs}
              action={allActions.MaterialDetailsActions.fetchMaterialChangelogs}
              title="MRP Changelog"
              tabOptions={tabOptions.materialChangelogOptions}
              containerID="tabContainer3"
              data={materialChangelogs}
              clientHeight={clientHeight}
            />
          </div>
        </TabPane>
        <TabPane forceRender tab={<span className={functionalLocation?.length > 0 ? 'hasContent' : 'noContent'}>Func Loc</span>} key="FL">
          <div id="tabContainer4" style={{ height: '100%', overflowX: 'hidden' }}>
            <TableTab
              loadingKey={loadingKeys.functionalLocation}
              selector={(state) => state.materialDetailsState.functionalLocation}
              action={allActions.MaterialDetailsActions.fetchFunctionalLocation}
              title="Functional Location"
              tabOptions={tabOptions.functionalLocationOptions}
              containerID="tabContainer4"
              data={functionalLocation}
              clientHeight={clientHeight - 25}
            />
          </div>
        </TabPane>
        <TabPane forceRender tab={<span className={openDocs?.length > 0 ? 'hasContent' : 'noContent'}>Open Docs</span>} key="OD">
          <div id="open-docs" style={{ height: '100%', overflowX: 'hidden' }}>
            <TableTab
              loadingKey={loadingKeys.openDocs}
              selector={(state) => state.materialDetailsState.openDocs}
              action={allActions.MaterialDetailsActions.fetchOpenDocs}
              title="Open Docs"
              tabOptions={tabOptions.openDocs}
              containerID="openDocsContainer"
              data={openDocs}
              clientHeight={clientHeight - 40}
            />
          </div>
        </TabPane>
        <TabPane forceRender tab={<span className={reservations?.length > 0 ? 'hasContent' : 'noContent'}>Reservations</span>} key="RES">
          <div id="tabContainer5" style={{ height: '100%', overflowX: 'hidden' }}>
            <TableTab
              loadingKey={loadingKeys.reservations}
              selector={(state) => state.materialDetailsState.reservations}
              action={allActions.MaterialDetailsActions.fetchReservations}
              title="Reservations"
              activeTabKey={activeTabKey}
              tabOptions={tabOptions.reservationsOptions}
              containerID="tabContainer5"
              data={reservations}
              openQtyResvalue={openQtyResvalue}
              clientHeight={clientHeight - 40}
            />
          </div>
        </TabPane>
        <TabPane forceRender tab={<span className={(filteredPurchasing || purchasing?.length > 0) ? 'hasContent' : 'noContent'}>Purchasing</span>} key="PUR">
          <div style={{ height: '100%', display: 'grid', gridTemplateRows: '40% 60%' }}>
            <div id="tabPurchasingGraph" style={{ height: '100%' }}>
              <TabGraph
                type={GRAPH_TYPES.purchasing}
                filteredData={filteredPurchasing}
                postingDate="PO_LINE_EDAM_POSTING_DT"
                containerID="tabPurchasingGraph"
                activeTabKey={activeTabKey}
              />
            </div>
            <div id="tabPurchasingTable" style={{ height: '100%', overflowX: 'hidden' }}>
              <TableTab
                loadingKey={loadingKeys.purchasing}
                selector={(state) => state.materialDetailsState.purchasing}
                action={allActions.MaterialDetailsActions.fetchPurchasing}
                title="Purchasing"
                tabOptions={tabOptions.purchasingOptions}
                updateFilterData={updateFilteredPurchasing}
                containerID="tabPurchasingTable"
                data={purchasing}
                lowerHeight
                clientHeight={(clientHeight * 0.6) - 65}
              />
            </div>
          </div>
        </TabPane>
        <TabPane forceRender tab={<span>Lead Time</span>} key="LT">
          <LeadTimeTab
            loadingKey={loadingKeys.materialLeadTime}
            materialLeadTime={materialLeadTime}
            materialLeadTimeInsight={materialLeadTimeInsight}
          />
        </TabPane>
        <TabPane forceRender tab={<span className={returnDocument?.length > 0 ? 'hasContent' : 'noContent'}>Returns</span>} key="RD">
          <div id="returnsDocument" style={{ height: '100%', overflowX: 'hidden' }}>
            <TableTab
              loadingKey={loadingKeys.returnDocument}
              selector={(state) => state.materialDetailsState.returnDocument}
              action={allActions.MaterialDetailsActions.fetchReturnDocument}
              tabOptions={tabOptions.returnTabOptions}
              title="Returns"
              containerID="returnsDocument"
              data={returnDocument}
              clientHeight={clientHeight}
            />
          </div>
        </TabPane>
        <TabPane forceRender tab={<span className={warehouse?.length > 0 ? 'hasContent' : 'noContent'}>Warehouse</span>} key="WH">
          <div id="tabContainer8" className={styles.warehouseTab}>
            <div>
              <TableTab
                loadingKey={loadingKeys.warehouse}
                selector={(state) => state.materialDetailsState.warehouse}
                action={allActions.MaterialDetailsActions.fetchWarehouse}
                title="Warehouse"
                containerID="tabContainer8"
                data={warehouse}
                tableTitle="WM Stock"
              />
            </div>
            <div style={{ width: '40%' }}>
              <FixedBin materialNumber={material} plant={plant} />
            </div>
          </div>
        </TabPane>
        {hasViewerAccess
          && (
            <TabPane forceRender tab={<span className={(changelogs?.length > 0 || plOrdRelData?.length > 0) ? 'hasContent' : 'noContent'}>IOTA Changelog</span>} key="ICL">
              <div
                id="tabContainer9"
                style={{
                  height: '100%', overflowX: 'hidden', display: 'grid', gridTemplateRows: '1fr 1fr',
                }}
              >
                <TableTab
                  loadingKey={loadingKeys.changelogs}
                  selector={(state) => state.materialDetailsState.changelogs}
                  action={allActions.MaterialDetailsActions.fetchChangelogs}
                  title="IOTA Change log"
                  tabOptions={tabOptions.iotaChangelogOptions}
                  containerID="tabContainer9"
                  data={changelogs}
                  clientHeight={clientHeight}
                />
                <PlOrdReleaseTab
                  clientHeight={clientHeight}
                  plant={plant}
                  material={material}
                  data={plOrdRelData}
                />
              </div>
            </TabPane>
          )}
        <TabPane forceRender tab={<span className={xPlant?.length > 0 ? 'hasContent' : 'noContent'}>Other Plants</span>} key="xplant">
          <div id="xPlantContainer" style={{ height: '100%', overflowX: 'hidden' }}>
            <TableTab
              loadingKey={loadingKeys.getXPlant}
              selector={(state) => state.materialDetailsState.xPlant}
              action={allActions.MaterialDetailsActions.fetchXPlant}
              title="Other Plants"
              containerID="xPlantContainer"
              data={fixKeyOnXPlant}
              clientHeight={clientHeight - 40}
            />
          </div>
        </TabPane>
      </Tabs>
    </Row>
  );
}

export default TabElements;
TabElements.defaultProps = {
  excludePM10: false,
};
TabElements.propTypes = {
  excludePM10: PropTypes.bool,
};
